/**
 * 取り込んだあとに取り出せる、元の CSV と同じ形（同じ列・データ開始行より前の行もそのまま）のファイル（#5697・#5737）。
 * - 失敗した行: 確認画面でエラーとして外した行。直した値があれば書き戻す（直して取り込み直せるように）
 * - 修正を反映した CSV: 外した行を除く全行に、確認画面で直した値を書き戻したもの
 *
 * 確認画面の行番号（rowNo）は、空行を除いた後のデータ開始行からの通し番号（1 始まり）。
 */

import { SHIPMENT_ORDER_CSV_IMPORT } from '@/constants/common/message.constants'

import { type ColumnMapping } from './column-mapping'
import { type CsvReadConfig, type ParsedCsv } from './csv-import-state'
import { type LogiFieldValue } from './logi-fields'
import { type CellOverrides } from './preview'

export type CutOut = {
  /** データ開始行より前の行（ヘッダー行など）。修正を反映した CSV の先頭にそのまま置く */
  leadingRows: string[][]
  /** 失敗した行の CSV の先頭に置く行。ヘッダー行の末尾に「エラー内容」の見出しを足したもの */
  failedLeadingRows: string[][]
  /**
   * 失敗した行（直した値を書き戻した、元の CSV の形の行）。末尾に外した理由（エラー内容）の列を足す。
   * 取り込み直すときは、規定フォーマットは知らない見出しの列を読まず、列を対応づける取込は対応づけなければ読まないので、
   * この列は取り込まれない
   */
  failedRows: string[][]
  /** 修正を反映した CSV の行（外した行を除く全行に、直した値を書き戻した、元の CSV の形の行） */
  importedRows: string[][]
  /**
   * 取り込んだ行のうち、CSV にある列の値を元と違う値に直した行の数。0 なら修正を反映した CSV は元の CSV と同じなので出さない。
   * 元に戻したセルや、一括入力で元から同じ値だった行、CSV に無い列への一括入力だけの行は数えない
   */
  editedRowCount: number
}

type Params = {
  parsed: ParsedCsv
  readConfig: CsvReadConfig
  /** 実効テーブルの列番号（開始列を起点にした 0 始まり）→ 項目 */
  mapping: ColumnMapping
  overrides: CellOverrides
  /** 確認画面でエラーとして外した行（rowNo） */
  excludedRowNos: readonly number[]
  /** 外した行の理由（rowNo → 確認画面に出ていたエラーの説明）。失敗した行の末尾の列に書く */
  failedReasons?: ReadonlyMap<number, string>
}

export const buildCutOut = ({
  parsed,
  readConfig,
  mapping,
  overrides,
  excludedRowNos,
  failedReasons = new Map()
}: Params): CutOut => {
  const startRowIndex = Math.max(0, readConfig.startRowNo - 1)
  const startColIndex = Math.max(0, readConfig.startColNo - 1)
  const columnOf = new Map<LogiFieldValue, number>()
  for (const [key, field] of Object.entries(mapping)) {
    if (field) columnOf.set(field, startColIndex + Number(key))
  }
  const rawRowOf = (rowNo: number): string[] => {
    const row = parsed.rows[startRowIndex + rowNo - 1] ?? []
    return Array.from(
      { length: Math.max(parsed.columnCount, row.length) },
      (_, i) => row[i] ?? ''
    )
  }
  const editedRowOf = (rowNo: number): string[] => {
    const row = rawRowOf(rowNo)
    for (const [field, value] of Object.entries(overrides[rowNo] ?? {})) {
      const column = columnOf.get(field as LogiFieldValue)
      if (column !== undefined && value !== undefined) row[column] = value
    }
    return row
  }
  const isEditedInCsv = (rowNo: number): boolean => {
    const raw = rawRowOf(rowNo)
    return Object.entries(overrides[rowNo] ?? {}).some(([field, after]) => {
      const column = columnOf.get(field as LogiFieldValue)
      return (
        after !== undefined && column !== undefined && raw[column] !== after
      )
    })
  }

  const excluded = new Set(excludedRowNos)
  const importedRowNos = Array.from(
    { length: Math.max(0, parsed.rows.length - startRowIndex) },
    (_, i) => i + 1
  ).filter((rowNo) => !excluded.has(rowNo))

  // 理由の列は元の CSV の一番右の列のさらに右に置く（行ごとに列数が違っても同じ列にそろえる）
  const reasonColumn = parsed.columnCount
  const withReason = (row: string[], reason: string): string[] => [
    ...Array.from({ length: reasonColumn }, (_, i) => row[i] ?? ''),
    reason
  ]
  const leadingRows = parsed.rows.slice(0, startRowIndex)
  const headerRowIndex = readConfig.hasHeader ? readConfig.headerRowNo - 1 : -1
  const failedLeadingRows = leadingRows.map((row, index) =>
    index === headerRowIndex
      ? withReason(row, SHIPMENT_ORDER_CSV_IMPORT.CUT_OUT_FAILED_REASON_HEADER)
      : row
  )

  return {
    leadingRows,
    failedLeadingRows,
    failedRows: [...excluded]
      .sort((a, b) => a - b)
      .map((rowNo) =>
        withReason(editedRowOf(rowNo), failedReasons.get(rowNo) ?? '')
      ),
    importedRows: importedRowNos.map(editedRowOf),
    editedRowCount: importedRowNos.filter(isEditedInCsv).length
  }
}

export type SuntoryChange = {
  /** 取り込む行の番号（確認画面の行番号） */
  rowNo: number
  /** 行を見分けるための明細ID（元の CSV で引ける） */
  detailId: string | null
  field: LogiFieldValue
  before: string
  after: string
}

/**
 * サントリー様の修正箇所の一覧（#5697）。何行目の何を、何から何に直したか。
 * 修正を反映した CSV に書き戻せない項目（配送種別・荷姿・車種・パートナー）の直しは、ここにだけ残る。
 * 修正前の値は、直した値が無い状態で確かめた結果の行（baseRows）から引く（確かめ直した結果の行は直した後の値になる）。
 */
export const buildSuntoryChanges = (
  baseRows: ReadonlyArray<{
    rowNo: number
    cells: ReadonlyArray<{ field: string; value: string }>
  }>,
  overrides: Record<string, Partial<Record<string, string>> | undefined>
): SuntoryChange[] => {
  const baseByRow = new Map(
    baseRows.map((row) => [
      row.rowNo,
      new Map(row.cells.map((c) => [c.field, c.value]))
    ])
  )
  return Object.entries(overrides)
    .map(([rowNo, cells]) => [Number(rowNo), cells ?? {}] as const)
    .sort(([a], [b]) => a - b)
    .flatMap(([rowNo, cells]) => {
      const base = baseByRow.get(rowNo)
      return Object.entries(cells).flatMap(([field, after]) => {
        const before = base?.get(field) ?? ''
        if (after === undefined || before === after) return []
        return [
          {
            rowNo,
            detailId: base?.get('externalKey') ?? null,
            field: field as LogiFieldValue,
            before,
            after
          }
        ]
      })
    })
}

/** サントリー様の CSV のうち、直した値を書き戻せる項目（荷主・発地・着地）。 */
export const SUNTORY_WRITABLE_FIELDS: ReadonlySet<LogiFieldValue> =
  new Set<LogiFieldValue>(['customerId', 'originSiteId', 'destSiteId'])

/** 列の位置（A=0）。得意先のパーサーは列を位置で読む */
const columnIndexOf = (letters: string): number =>
  [...letters].reduce((n, c) => n * 26 + (c.charCodeAt(0) - 64), 0) - 1

/**
 * 直した値を書き戻す列。パーサー（BE の suntory-*-csv-parser.ts）と同じ列を指す。
 * - 得意先（dedicated）: 列の位置で読む（運送会社コード=AH・払・受払場所=Z・届先コード=K・明細ＩＤ=CJ）
 * - 社内移動（internal）: 見出し名で読む。着地は出荷タイプが社内移動なら受・受払場所、得意先出荷なら届先コード
 */
const suntoryColumnsOf = (
  kind: 'internal' | 'dedicated',
  header: readonly string[],
  row: readonly string[]
): { detailId: number; columns: Partial<Record<LogiFieldValue, number>> } => {
  if (kind === 'dedicated') {
    return {
      detailId: columnIndexOf('CJ'),
      columns: {
        customerId: columnIndexOf('AH'),
        originSiteId: columnIndexOf('Z'),
        destSiteId: columnIndexOf('K')
      }
    }
  }
  const H = SHIPMENT_ORDER_CSV_IMPORT.SUNTORY_INTERNAL_COLUMNS
  const at = (name: string) => header.indexOf(name)
  const isInternalMove =
    unwrapExcelCode(row[at(H.SHIPMENT_TYPE)] ?? '') === H.INTERNAL_MOVE
  return {
    detailId: at(H.DETAIL_ID),
    columns: {
      customerId: at(H.CARRIER_CODE),
      originSiteId: at(H.ORIGIN),
      destSiteId: at(isInternalMove ? H.DEST_INTERNAL_MOVE : H.DEST_CUSTOMER)
    }
  }
}

/** パーサーの formatCode と同じ: ="..." の囲みを外す */
const unwrapExcelCode = (cell: string): string => {
  const match = cell.match(/^="(.+)"$/)
  return match ? match[1] : cell
}

/** 元のセルが ="..." で囲まれていれば、直した値も同じ囲みで書く（パーサーが同じく囲みを外して読む） */
const rewrapLike = (original: string, value: string): string =>
  /^=".*"$/.test(original) ? `="${value}"` : value

/**
 * サントリー様の修正を反映した CSV（#5697・#5737）。元の CSV（ヘッダー行を含む生のセル）の全行に、直した値のうち
 * 書き戻せる項目（荷主・発地・着地）を元の列へ書き戻して返す。先頭はヘッダー行。
 * 確認画面の行番号は CSV の行と一致しない（社内移動は読み飛ばした行を数えない）ので、明細ID で行を突き合わせる。
 * 書き戻せない項目（配送種別は出荷タイプの値で読み方が決まる・荷姿とパートナーは列が無い・車種は読まない）は
 * 修正箇所の一覧にだけ出る。
 */
export const buildSuntoryImportedRows = ({
  kind,
  csvRows,
  changes
}: {
  kind: 'internal' | 'dedicated'
  /** 元の CSV の行（1 行目がヘッダー、セルは ="..." の囲みを外していない生の値） */
  csvRows: readonly (readonly string[])[]
  changes: readonly SuntoryChange[]
}): string[][] => {
  const [header = [], ...dataRows] = csvRows
  const byDetailId = new Map<string, SuntoryChange[]>()
  for (const c of changes) {
    if (!SUNTORY_WRITABLE_FIELDS.has(c.field) || c.detailId === null) continue
    byDetailId.set(c.detailId, [...(byDetailId.get(c.detailId) ?? []), c])
  }
  const rows = dataRows.map((row) => {
    const { detailId, columns } = suntoryColumnsOf(kind, header, row)
    const out = [...row]
    for (const c of byDetailId.get(unwrapExcelCode(row[detailId] ?? '')) ??
      []) {
      const column = columns[c.field]
      if (column === undefined || column < 0) continue
      out[column] = rewrapLike(row[column] ?? '', c.after)
    }
    return out
  })
  return [[...header], ...rows]
}
